/**
 * CPR (Credit Pulse Report) / EMIPOD Service – raw request wrappers.
 * Migrated from 3in1cms bajajfinserv-web/ui.solid/src/lib/connectionManager/cprApi.ts
 *
 * This module is intentionally thin: it exposes one typed function per
 * EMIPOD endpoint and delegates all payload assembly, session/cookie handling,
 * and response normalisation to the consuming module (bfl-cpr-libs).
 *
 * Endpoints:
 *  - offerSelectionRequest     → emipod/crosssell/offerselection
 *  - preapplicationRequest     → emipod/crosssell/preapplication
 *  - additionalDataRequest     → emipod/crosssell/additionaldataprocess
 *  - csCustomerPaymentRequest  → emipod/crosssell/cscustomerpayment
 */

import { request } from "../core/request";
import { OPERATION_IDS, REST_CONNECTION_FROM } from "./constants";
import type { IEmipodRes, IOfferSelectionReq } from "../types/cprTypes";

/**
 * Raw OfferSelection API call.
 * Caller is responsible for building the full `requestData` payload.
 */
export const offerSelectionRequest = async (
  requestData: IOfferSelectionReq
): Promise<IEmipodRes> => {
  return request<IEmipodRes, IOfferSelectionReq>({
    operationId: OPERATION_IDS.offerSelection,
    requestData,
    connectionFrom: REST_CONNECTION_FROM,
  });
};

/**
 * Raw PreApplication API call.
 * Caller is responsible for building the full `requestData` payload.
 * Typed as Record<string, unknown> because the enriched payload differs from IPreapplicationReq shape.
 */
export const preapplicationRequest = async (
  requestData: Record<string, unknown>
): Promise<IEmipodRes> => {
  return request<IEmipodRes>({
    operationId: OPERATION_IDS.preapplication,
    requestData,
    connectionFrom: REST_CONNECTION_FROM,
  });
};

/**
 * Raw AdditionalDataProcess API call.
 * Caller is responsible for building the full `requestData` payload.
 * Typed as Record<string, unknown> because the enriched payload differs from IAdditionalDataReq shape.
 */
export const additionalDataRequest = async (
  requestData: Record<string, unknown>
): Promise<IEmipodRes> => {
  return request<IEmipodRes>({
    operationId: OPERATION_IDS.additionaldataprocess,
    requestData,
    connectionFrom: REST_CONNECTION_FROM,
  });
};

/**
 * Raw CSCustomerPayment API call.
 * Caller is responsible for building the full `requestData` payload.
 * Typed as Record<string, unknown> because the enriched payload differs from ICscustomerPaymentReq shape.
 */
export const csCustomerPaymentRequest = async (
  requestData: Record<string, unknown>
): Promise<IEmipodRes> => {
  return request<IEmipodRes>({
    operationId: OPERATION_IDS.csCustomerPayment,
    requestData,
    connectionFrom: REST_CONNECTION_FROM,
  });
};
